import { formatCount, tt } from '@agos/shared'

import type { StockItemLike } from './goods-receipt'

/**
 * Renaming a stock category.
 *
 * `stock_items.category` is a **string on every row**, not a table — there is no category entity and no
 * endpoint that renames one. So a rename is `PATCH /stock/items/{id}` per item, and *Zelenina* is 25
 * items in this data.
 *
 * **That makes a partial failure worse than a refusal, and it is the reason this screen exists rather
 * than a text field on each row.** If twelve of twenty-five patches land, the category is now two
 * categories — half the items under *Zelenina* and half under whatever was typed — and every list,
 * filter and report groups them apart. Nothing errors; the stock room simply appears to have grown a
 * category overnight.
 *
 * So: the count is said before the button, the failures are named individually afterwards, and the
 * screen states plainly that a half-finished rename leaves two categories rather than one. Retrying is
 * safe — an item already renamed is not in the next plan.
 *
 * Measured categories: Zelenina 25, Špajza 19, Nápoje nealko 18, Nápoje alko 13, Dresing a omáčky 11,
 * Mäso 11, Dezerty 10, Syr 9.
 */

export interface CategoryCount {
  name: string
  count: number
}

/** Every category in use, with how many items carry it, biggest first. */
export function categoriesOf(items: readonly StockItemLike[]): CategoryCount[] {
  const counts = new Map<string, number>()
  for (const item of items) {
    const name = ((item as { category?: unknown }).category ?? '') as string
    if (typeof name !== 'string' || name.trim() === '') continue
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name, 'sk'))
}

export function itemsInCategory(
  items: readonly StockItemLike[],
  category: string,
): StockItemLike[] {
  return items.filter((item) => ((item as { category?: unknown }).category ?? '') === category)
}

/**
 * What the server would refuse, or what would quietly do nothing.
 *
 * Each carries its own severity rather than being classified afterwards by matching on its words — a
 * message is a sentence somebody may reword, and a rule that reads it is a rule that breaks when they do.
 */
/**
 * The existing category a typed name really means, if there is one.
 *
 * A stock category is **not a row**: `stock_items.category` is a text column, so a category exists
 * only because some item carries that string. Which means a typo does not fail — it silently creates a
 * second category, and the stock room appears to have grown one overnight. `Zelenina`, `zelenina` and
 * `Zelenina ` are three categories to the database and one to the person typing.
 *
 * That is what the owner's note is about: *"aktuálne sa to robí pri vytváraní položky.. hrozné"*. The
 * item editor offered a free-text box and a **Pridať** button that only filled the field, with nothing
 * comparing what was typed against what already existed.
 *
 * Case and surrounding space are folded; the middle is not. *Suché plody* and *Suche plody* are left
 * as two, because stripping diacritics would merge words that genuinely differ in Slovak.
 */
export function matchingCategory(
  existing: readonly string[],
  typed: string,
): string | null {
  const needle = typed.trim().replace(/\s+/g, ' ').toLocaleLowerCase('sk')
  if (needle === '') return null
  return (
    existing.find(
      (category) => category.trim().replace(/\s+/g, ' ').toLocaleLowerCase('sk') === needle,
    ) ?? null
  )
}

export interface RenameProblem {
  message: string
  blocking: boolean
}

export function renameProblems(
  from: string,
  to: string,
  items: readonly StockItemLike[],
): RenameProblem[] {
  const target = to.trim()
  const found: RenameProblem[] = []
  if (from === '') found.push({ message: tt('Vyberte kategóriu.'), blocking: true })
  if (target === '') found.push({ message: tt('Zadajte nový názov.'), blocking: true })
  else if (target === from) {
    found.push({ message: tt('Nový názov je rovnaký ako starý.'), blocking: true })
  } else if (matchingCategory(categoriesOf(items).map((category) => category.name), target) !== null) {
    // Not an error — merging two categories is a legitimate thing to want — but it is not what
    // somebody fixing a typo means, so it is said rather than done silently.
    found.push({
      message: `${tt('Kategória')} „${target}" ${tt('už existuje - premenovaním sa obe zlúčia do jednej.')}`,
      blocking: false,
    })
  }
  return found
}

export function isBlocking(problems: readonly RenameProblem[]): boolean {
  return problems.some((problem) => problem.blocking)
}

/** The ids this rename would write, so a retry does not touch what already moved. */
export function idsToRename(items: readonly StockItemLike[], from: string): number[] {
  return itemsInCategory(items, from).flatMap((item) => (item.id === undefined ? [] : [item.id]))
}

export function renamePayload(to: string): Record<string, unknown> {
  return { category: to.trim() }
}

/**
 * Said before the button.
 *
 * **What this says changed on 2026-08-05, because what happens changed.** §10A.16 made a category a
 * row, so a rename is one `PATCH /stock-item-categories/{id}` that moves the row and every item's
 * text in one transaction. The count is still said — 25 items is worth knowing before you press it —
 * but the warning that follows it is not, because the half-finished state it described is now
 * unreachable.
 *
 * A category with **no row** still takes the old path and still earns the old warning. That is not
 * dead code: the item editor writes `category` as free text, so typing a new name there creates a
 * category the table has never seen.
 */
export function planNote(
  items: readonly StockItemLike[],
  from: string,
  to: string,
  categoryId: number | null = null,
): string {
  const count = idsToRename(items, from).length
  if (categoryId !== null) {
    return count === 0
      ? `${tt('Premenuje sa na')} „${to.trim()}".`
      : `${formatCount(count)} ${tt('položiek sa prepíše na')} „${to.trim()}" ${tt('jednou zmenou.')}`
  }
  if (count === 0) return tt('V tejto kategórii nie sú žiadne položky.')
  return `${formatCount(count)} ${tt('položiek sa prepíše na')} „${to.trim()}". ${tt('Nie je to jedna zmena, ale')} ${formatCount(count)} ${tt('- ak sa časť nepodarí, kategória zostane rozdelená na dve. Zvyšok sa dá dokončiť opakovaním.')}`
}

/**
 * The row behind a category name, if the table has one — §10A.16.
 *
 * Matched the same way {@link matchingCategory} matches: case and surrounding space folded, the
 * middle left alone. A category the table does not know is not an error; it is a name somebody typed
 * into the item editor, which still writes free text.
 */
export function categoryRowFor(
  rows: readonly { id?: number; name?: string }[],
  name: string,
): number | null {
  const needle = name.trim().replace(/\s+/g, ' ').toLocaleLowerCase('sk')
  if (needle === '') return null
  const row = rows.find(
    (candidate) =>
      (candidate.name ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('sk') === needle,
  )
  return row?.id ?? null
}

/** Said afterwards, naming what did not move rather than reporting a flat success. */
export function outcomeNote(renamed: number, failed: readonly string[]): string {
  if (failed.length === 0) return `${tt('Premenované')}: ${formatCount(renamed)}`
  return `${tt('Premenované')}: ${formatCount(renamed)}. ${tt('Nepodarilo sa')}: ${failed.join(', ')} - ${tt('kategória je zatiaľ rozdelená; zopakujte premenovanie.')}`
}

/** A category as the management list shows it: the row where there is one, plus its item count. */
export interface ManageableCategory {
  id: number | null
  name: string
  count: number
}

/**
 * The categories to manage — the table's rows, plus any name only the items know about.
 *
 * §10A.16 keeps the table in step on every item write, so the second group should be empty. It is
 * still merged in rather than assumed away: a category written before that landed has no row, and
 * dropping it from this list would hide a category the stock room is visibly using. Those rows carry
 * `id: null`, which is what the screen keys reorder and delete off — neither is possible without one.
 *
 * The table's order wins, because `sort_order` is the thing somebody dragged into place. Item-only
 * names follow, biggest first, so they are visible without displacing a chosen order.
 *
 * The count comes from the items in both cases rather than from the row's `itemCount`: the list is
 * rendered beside a table of those same items, and two numbers disagreeing on one screen is worse
 * than either being slightly stale.
 */
export function manageableCategories(
  rows: readonly { id?: number; name?: string; sortOrder?: number }[],
  items: readonly StockItemLike[],
): ManageableCategory[] {
  const counts = new Map(categoriesOf(items).map((category) => [category.name, category.count]))
  const fromRows = [...rows]
    .filter((row) => (row.name ?? '').trim() !== '')
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
    .map((row) => ({
      id: row.id ?? null,
      name: row.name as string,
      count: counts.get(row.name as string) ?? 0,
    }))

  const claimed = new Set(fromRows.map((row) => row.name.trim().toLocaleLowerCase('sk')))
  const orphans = categoriesOf(items)
    .filter((category) => !claimed.has(category.name.trim().toLocaleLowerCase('sk')))
    .map((category) => ({ id: null, name: category.name, count: category.count }))

  return [...fromRows, ...orphans]
}

/**
 * The two writes a one-step reorder is: this row and the one it swaps with.
 *
 * Returned as a plan rather than performed, so the rule is testable and the screen stays the thing
 * that talks to the server. An out-of-range move is an empty plan — the buttons are disabled at the
 * ends, and a handler that trusted that would be one refactor from writing a negative sort order.
 */
export function reorderPlan(
  categories: readonly ManageableCategory[],
  index: number,
  delta: number,
): { id: number; sortOrder: number }[] {
  const target = index + delta
  const moving = categories[index]
  const displaced = categories[target]
  if (!moving || !displaced || moving.id === null || displaced.id === null) return []
  return [
    { id: moving.id, sortOrder: target + 1 },
    { id: displaced.id, sortOrder: index + 1 },
  ]
}
